import React, { useContext, useRef, useState } from 'react'
import {
  FetchBillingResponse,
  PaginationResponse,
  PaymentConfigurationCodeEnum,
} from '../../../../swagger'
import { BillingFilters } from '../interfaces'
import IntlAccountingTransactionModal from '../../../Modals/IntlAccountingTransactionModal'
import LicensingFiltersModal from '../../../Modals/LicensingFiltersModal'
import {
  ActionableTable,
  ActionableTableColumn,
} from '../../../Table/ActionableTable'
import { Box, styled, TableContainer, useTheme } from '@mui/material'
import { GridInputRowSelectionModel, GridRowParams } from '@mui/x-data-grid'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import { useParams } from 'react-router'
import { LoadingContext } from '../../../Context/LoadingContext'
import { useTranslation } from 'react-i18next'
import LCDHeader from '../LCDLicensingTable/LCDHeader'
import FiltersBar from '../LCDLicensingTable/FiltersBar'
import {
  getLCDLicensingHeaders,
  mapBillingToLCDLicensingRows,
} from '../LCDLicensingTable/utils'
import { useAuth } from '../../../Routes/AuthProvider'
import LCDLicensingBreakdown from '../LCDLicensingBreakdown'

const StyledTableContainer = styled(TableContainer)({
  paddingLeft: 0,
})

interface LCDLicensingRow {
  communityName: string
  program: string
  tutorName: string
  tutorOwes: number
  communityDirectorOwes: number
  programKey: number
  userKey: number
}

export type LCDLicensingTableProps = {
  billingData: FetchBillingResponse
  pagination: PaginationResponse
  handlePagination: (_pagination: PaginationResponse) => void
  filters: BillingFilters
  setFilters: React.Dispatch<React.SetStateAction<BillingFilters>>
}

const LCDLicensingTable: React.FC<LCDLicensingTableProps> = ({
  pagination,
  handlePagination,
  filters,
  setFilters,
  billingData,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { userId } = useParams()
  const {
    BillingTab: { fetchBilling: fetchBillingLoadingId },
  } = useLoadingIds()
  const { loadingIds } = useContext(LoadingContext)

  const { userDetails } = useAuth()

  /** State Objects */
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false)
  const [
    isIntlAccountingTransactionModalOpen,
    setIsIntlAccountingTransactionModalOpen,
  ] = useState(false)

  const [selectedRow, setSelectedRow] = useState({
    programKey: 0,
    programName: '',
    communityName: '',
  })

  const filtersModalRef = useRef<{
    clearFilter: (filterName: keyof BillingFilters) => void
  }>(null)

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridInputRowSelectionModel>([])

  const detailsBoxRef = useRef<HTMLDivElement>(null)
  const scrollToDetails = () =>
    detailsBoxRef?.current?.scrollIntoView({ behavior: 'smooth' })

  /** Methods */

  const handleLicensingTableRowClick = (
    params: GridRowParams<LCDLicensingRow>
  ) => {
    setRowSelectionModel(params.id)
    setSelectedRow({
      programKey: params.row.programKey,
      programName: params.row.program,
      communityName: params.row.communityName,
    })
    scrollToDetails()
  }

  const handleRemoveFilter = (filter: keyof BillingFilters) => {
    if (filtersModalRef.current) {
      filtersModalRef.current.clearFilter(filter)
    }
  }

  /** Other Objects */
  const tableHeaders: ActionableTableColumn[] = getLCDLicensingHeaders(t)

  const lcdLicensingRows = mapBillingToLCDLicensingRows(billingData)
  const achDiscount =
    billingData.configurations.find(
      (configuration) =>
        configuration.code === PaymentConfigurationCodeEnum.AchDiscount
    )?.amount ?? '0.00'
  const billingDataLoadingId = fetchBillingLoadingId + JSON.stringify(filters)

  return (
    <>
      <Box margin={theme.spacing(2, 3, 0, 2)} id="LCDLicensingTableContainer">
        <StyledTableContainer>
          <LCDHeader />

          {!!selectedRow.programKey && (
            <Box ref={detailsBoxRef} margin={theme.spacing(2, 3, 0, 2)}>
              <LCDLicensingBreakdown
                programKey={selectedRow.programKey}
                programName={selectedRow.programName}
                communityName={selectedRow.communityName}
                achDiscount={achDiscount}
                billingDataLoadingId={billingDataLoadingId}
                achAllowed={billingData.achAllowed}
              />
            </Box>
          )}
          <FiltersBar
            onOpenFiltersModal={() => setIsFiltersModalOpen(true)}
            onRemoveFilter={handleRemoveFilter}
            filters={filters}
          />
          <ActionableTable
            isLoading={loadingIds.has(billingDataLoadingId)}
            columns={tableHeaders}
            rows={lcdLicensingRows}
            ariaLabelledBy={'LCDLicensingTableHeader'}
            noResultsMessage={t(
              'LCDLicensingTable.Transactions.NoPreviousTransactions',
              'No transaction history'
            )}
            onRowClick={handleLicensingTableRowClick}
            rowHeight={50}
            rowSelectionModel={rowSelectionModel}
            pagination={
              !!pagination
                ? {
                    ...pagination,
                    page: pagination.page - 1,
                  }
                : undefined
            }
            handlePaginationChange={(params: PaginationResponse) =>
              handlePagination?.({ ...params, page: params.page + 1 })
            }
            customCssTable={{
              '& .MuiDataGrid-row:hover': {
                cursor: 'pointer',
              },
            }}
          />
        </StyledTableContainer>
      </Box>

      {/* **************** MODALS **************** */}
      <LicensingFiltersModal
        /**
         * This hack is necessary to avoid some jest warnings.
         */
        ref={process.env.NODE_ENV === 'test' ? undefined : filtersModalRef}
        isOpen={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        onFilter={(params) => setFilters?.(params)}
        actorKey={Number(userDetails.actingAs) ?? 0}
        showActorFilter={false}
      />
      <IntlAccountingTransactionModal
        isOpen={isIntlAccountingTransactionModalOpen}
        onClose={() => setIsIntlAccountingTransactionModalOpen(false)}
        userKey={Number(userId) ?? 0}
        actorKey={Number(userDetails.actingAs) ?? 0}
      />
    </>
  )
}

export default LCDLicensingTable
